/**
 * CopyToOtherDraaidagenDialog — Stitch design
 */
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, TextField, InputAdornment, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Checkbox, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { toast } from 'react-hot-toast';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

export default function CopyToOtherDraaidagenDialog({
  open, onClose, draaidagen = [], currentDraaidagId, selectedMaterials = [],
  currentReservation, fetchReservations, extraDagVoor, extraDagNa,
}) {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [copying, setCopying] = useState(false);
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const availableDraaidagen = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return draaidagen
      .filter(d => {
        if (d.id === currentDraaidagId) return false;
        const raw = d.fields?.StartdatumOutlook || d.fields?.StartDatum || '';
        const dateStr = typeof raw === 'string' && raw.includes('T') ? raw.replace(/Z$/, '') + (raw.endsWith('Z') ? '' : '') : raw;
        const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        if (isNaN(date.getTime())) return false;
        return date >= today;
      })
      .filter(d => !search || (d.fields?.Draaidag || '').toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (a.fields?.StartdatumOutlook || a.fields?.StartDatum || '').localeCompare(b.fields?.StartdatumOutlook || b.fields?.StartDatum || ''))
      .slice(0, 50);
  }, [draaidagen, currentDraaidagId, search]);

  const toggle = (id) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const handleCopy = async () => {
    if (selected.size === 0) return;
    setCopying(true);
    const toastId = toast.loading(`Kopiëren naar ${selected.size} draaidagen...`);
    const materialIds = selectedMaterials.map(m => m.id);
    let success = 0, fail = 0;
    for (const draaidagId of selected) {
      try {
        const draaidag = draaidagen.find(d => d.id === draaidagId);
        const startDate = draaidag?.fields?.StartDatum || draaidag?.fields?.StartdatumOutlook?.substring(0, 10) || '';
        const endDate = draaidag?.fields?.EindDatum || draaidag?.fields?.EinddatumOutlook?.substring(0, 10) || startDate;
        await axios.post(API_ENDPOINTS.reservations.save(), { draaidagId, materialIds, startDate, endDate, extraDagNa: extraDagNa || false, extraDagVoor: extraDagVoor || false });
        success++;
      } catch (e) { console.error(`Copy to ${draaidagId} failed:`, e.message); fail++; }
    }
    if (fail === 0) toast.success(`${success} reserveringen aangemaakt!`, { id: toastId });
    else toast.error(`${success} gelukt, ${fail} mislukt`, { id: toastId });
    setCopying(false); setSelected(new Set()); onClose();
    if (fetchReservations) setTimeout(() => fetchReservations(true), 500);
  };

  const formatDate = (d) => {
    const raw = d.fields?.StartdatumOutlook || d.fields?.StartDatum || '';
    return raw.includes('T') ? new Date(raw.endsWith('Z') ? raw : raw + 'Z').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : raw;
  };

  const getShortTitle = (d) => {
    const title = d.fields?.Draaidag || '';
    const pipeIdx = title.indexOf('||');
    if (pipeIdx > 0) { const crew = title.substring(0, pipeIdx).trim(); const project = title.substring(pipeIdx + 2).trim().replace(/\s*-\s*\[\d+\].*$/, '').replace(/\s*-\s*[A-Z]{2}\s*$/, '').trim(); return crew ? `${crew}: ${project}` : project; }
    return title.substring(0, 50);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: { xs: 0, sm: '0.75rem' } } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="content_copy" size={20} sx={{ color: T.primary }} />
          <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 800, color: T.text }}>
            Kopieer naar andere draaidagen
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mt: 0.5 }}>
          Maak dezelfde reservering ({selectedMaterials.length} materialen) voor andere draaidagen
        </Typography>
      </Box>
      <DialogContent>
        <TextField fullWidth size="small" placeholder="Zoek draaidag..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outlineVariant }} /></InputAdornment> }}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody, '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' } } }}
        />
        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {availableDraaidagen.length === 0 ? (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant, py: 2, textAlign: 'center' }}>Geen toekomstige draaidagen gevonden</Typography>
          ) : (
            <List dense>
              {availableDraaidagen.map(d => {
                const isSelected = selected.has(d.id);
                const hasReservation = d.fields?.AantalMateriaalGereserveerd > 0 || d.fields?.['Geen Materiaal Nodig'];
                return (
                  <ListItem key={d.id} disablePadding>
                    <ListItemButton onClick={() => toggle(d.id)} dense selected={isSelected} sx={{ borderRadius: '0.35rem' }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={isSelected} size="small" sx={{ '&.Mui-checked': { color: T.primary } }} />
                      </ListItemIcon>
                      <ListItemText primary={getShortTitle(d)} secondary={formatDate(d)}
                        primaryTypographyProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 500 }, noWrap: true }}
                        secondaryTypographyProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.68rem' } }} />
                      {hasReservation && (
                        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: T.amber, bgcolor: T.amberBg, px: 0.75, py: 0.15, borderRadius: '9999px' }}>
                          Heeft al reservering
                        </Typography>
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box onClick={!copying ? onClose : undefined} sx={{ px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>
          Annuleren
        </Box>
        <Box onClick={!copying && selected.size > 0 ? handleCopy : undefined} sx={{
          px: 2.5, py: 0.6, borderRadius: '0.5rem',
          cursor: !copying && selected.size > 0 ? 'pointer' : 'default',
          background: !copying && selected.size > 0 ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)` : T.surfaceLow,
          color: !copying && selected.size > 0 ? '#fff' : T.outlineVariant,
          fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
          boxShadow: !copying && selected.size > 0 ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
          display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.2s',
        }}>
          <Icon name={copying ? 'hourglass_empty' : 'content_copy'} size={16} sx={{ color: 'inherit' }} />
          {copying ? 'Kopiëren…' : `Kopieer naar ${selected.size} draaidag${selected.size !== 1 ? 'en' : ''}`}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
